import React from 'react';
import Icon from '../../../components/AppIcon';

const FounderProfileSection = () => {
  const founders = [
    {
      id: 1,
      name: 'Emir Causevic',
      role: 'CEO & Co-Founder',
      bio: `Emir Causevic is the CEO and Co-Founder of Sleeve GmbH, founded from firsthand experience working inside complex, safety-critical systems where innovation was possible—but rarely easy.

During his career in embedded, automotive, and industrial software at companies such as TTTech Auto and Thales, Emir repeatedly encountered the same problem: engineers were slowed down by limited hardware access, long setup cycles, and tooling that created friction instead of momentum. Sleeve was created to change that.

With a background in computer engineering and years of hands-on technical and product leadership, Emir set out to build a platform that makes advanced computing systems practical, accessible, and scalable for real-world engineering teams.

Today, he leads Sleeve's vision and product strategy, driven by a simple belief: when barriers are removed, engineers move faster—and better technology follows.`,quote: '"When you remove friction from engineering, progress stops being theoretical—and starts becoming real."',icon: 'User',color: 'primary',image: '/assets/images/Emir-1769693551516.png'
    },
    {
      id: 2,
      name: 'Irmin Okic',role: 'Co-Founder & CTO',
      bio: `Irmin Okic is the Co-Founder and CTO of Sleeve GmbH, where he leads the platform's technical architecture with a focus on reliability, scalability, and engineering excellence.

Irmin's journey began as a full-stack software engineer working across distributed systems, cloud infrastructure, and IoT-driven platforms. Through years of hands-on development—spanning enterprise environments, system integration, and cyber-physical systems—he saw how quickly technical complexity could grow when software met real-world hardware.

Sleeve was founded to address that reality. Together with the founding team, Irmin set out to build a platform that treats physical devices as first-class citizens of modern cloud infrastructure, without sacrificing performance, security, or architectural clarity.

With a background in software engineering and deep experience in cloud, DevOps, and distributed systems, Irmin ensures Sleeve's technology remains robust, maintainable, and built to scale.`,
      quote: '"Great systems aren\'t just functional—they\'re built to last, adapt, and disappear into the background."',
      icon: 'Code',
      color: 'accent',
      image: '/assets/images/Irmin-1769693620217.png'
    }
  ];

  return (
    <section className="py-24 px-4 md:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-16 md:space-y-20 lg:space-y-24">
        {founders?.map((founder) => (
          <div
            key={founder?.id}
            className="bg-card border border-border rounded-2xl p-8 md:p-10 lg:p-12"
          >
            {/* LEFT-RIGHT LAYOUT */}
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              {/* LEFT COLUMN: Image, Name, Title */}
              <div className="flex-shrink-0 lg:w-64">
                {/* Profile Image */}
                {founder?.image ? (
                  <div className="w-full aspect-square rounded-lg overflow-hidden mb-6">
                    <img 
                      src={founder?.image} 
                      alt={founder?.name}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center' }}
                    />
                  </div>
                ) : (
                  <div 
                    className="w-full aspect-square rounded-lg flex items-center justify-center mb-6"
                    style={{ background: `var(--color-${founder?.color})15` }}
                  >
                    <Icon name={founder?.icon} size={32} color={`var(--color-${founder?.color})`} />
                  </div>
                )}
                
                {/* Name */}
                <h3 className="text-xl font-bold text-foreground mb-2">
                  {founder?.name}
                </h3>
                
                {/* Title */}
                <p className="text-base md:text-lg text-muted-foreground">
                  {founder?.role}
                </p>
              </div>

              {/* RIGHT COLUMN: Bio and Quote */}
              <div className="flex-1 min-w-0">
                {/* Bio */}
                <div className="mb-8 space-y-4">
                  {founder?.bio?.split('\n\n')?.map((paragraph, index) => (
                    <p key={index} className="text-sm md:text-base text-muted-foreground leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {/* Pull Quote */}
                <div className="border-l-4 border-primary pl-6 py-2">
                  <p className="text-base md:text-lg text-foreground italic leading-relaxed">
                    {founder?.quote}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FounderProfileSection;